import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  Input,
  PLATFORM_ID,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { CountriesService } from '../../shared/services/countries.service';
import { environment } from '../../../environments/environment';

type NavCountry = { slug: string; name: string };

@Component({
  selector: 'app-marketing-header',
  standalone: true,
  imports: [RouterLink, RouterLinkActive],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'cg-mkt-header-host',
    style: 'display: block; width: 100%; max-width: 100%;',
  },
  template: `
    <header class="cg-mkt-header">
      <div class="cg-mkt-header__shell">
        <div class="cg-mkt-header__inner">
          <a routerLink="/" class="cg-mkt-logo" (click)="closeMobileMenu()">
          <span class="cg-mkt-logo__img-frame">
            <img
              class="cg-mkt-logo__img"
              src="/assets/images/logo.webp"
              alt=""
              width="198"
              height="54"
            />
          </span>
          <span class="cg-mkt-logo__wordmark">Career Grad Global</span>
          @if (showDevBadge) {
            <span class="cg-mkt-logo__env-badge">DEV</span>
          }
        </a>

        <nav class="cg-mkt-nav" aria-label="Primary">
          <a
            routerLink="/"
            class="cg-mkt-nav__link"
            routerLinkActive="cg-mkt-nav__link--active"
            [routerLinkActiveOptions]="{ exact: true }"
            >Home</a
          >

          <div
            #destinationsRoot
            class="cg-mkt-nav__item"
            [class.cg-mkt-nav__item--open]="destinationsOpen()"
          >
            <button
              type="button"
              class="cg-mkt-nav__link cg-mkt-nav__trigger"
              [attr.aria-expanded]="destinationsOpen()"
              aria-haspopup="true"
              (click)="toggleDestinations($event)"
            >
              Destinations
              <span class="cg-mkt-nav__chevron" aria-hidden="true">▾</span>
            </button>
            @if (countries().length > 0) {
              <ul
                class="cg-mkt-nav__menu"
                role="menu"
                [class.cg-mkt-nav__menu--open]="destinationsOpen()"
              >
                @for (c of countries(); track c.slug) {
                  <li role="none">
                    <a
                      [routerLink]="['/countries', c.slug]"
                      class="cg-mkt-nav__menu-link"
                      role="menuitem"
                      (click)="onMenuLinkClick()"
                    >
                      {{ c.name }}
                    </a>
                  </li>
                }
              </ul>
            } @else if (destinationsOpen()) {
              <ul class="cg-mkt-nav__menu cg-mkt-nav__menu--open" role="menu">
                <li class="cg-mkt-nav__menu-empty" role="none">No published destinations yet.</li>
              </ul>
            }
          </div>

          <a
            routerLink="/success-stories"
            class="cg-mkt-nav__link"
            routerLinkActive="cg-mkt-nav__link--active"
            >Success Stories</a
          >
          <a
            routerLink="/testimonials"
            class="cg-mkt-nav__link"
            routerLinkActive="cg-mkt-nav__link--active"
            >Testimonials</a
          >
          <a
            routerLink="/blogs"
            class="cg-mkt-nav__link"
            routerLinkActive="cg-mkt-nav__link--active"
            >Blogs</a
          >
          @if (variant === 'country') {
            <a href="#universities" class="cg-mkt-nav__link">Universities</a>
          }
        </nav>

        <div class="cg-mkt-header__ctas">
          <a routerLink="/auth/login" class="cg-mkt-btn cg-mkt-btn--ghost">Admin</a>
          <a href="#cta" class="cg-mkt-btn cg-mkt-btn--primary">Get started</a>
        </div>

        <div class="cg-mkt-header__mobile-actions">
          <button
            type="button"
            class="cg-mkt-header__menu-toggle"
            [attr.aria-expanded]="mobileMenuOpen()"
            aria-controls="cg-mkt-mobile-nav"
            [attr.aria-label]="mobileMenuOpen() ? 'Close menu' : 'Open menu'"
            (click)="toggleMobileMenu()"
          >
            <span class="cg-mkt-header__menu-icon" aria-hidden="true"></span>
          </button>
        </div>
        </div>
      </div>
    </header>

    <div
      id="cg-mkt-mobile-nav"
      class="cg-mkt-mobile-nav"
      [class.cg-mkt-mobile-nav--open]="mobileMenuOpen()"
      [attr.aria-hidden]="!mobileMenuOpen()"
    >
      <button
        type="button"
        class="cg-mkt-mobile-nav__backdrop"
        tabindex="-1"
        aria-label="Close menu"
        (click)="closeMobileMenu()"
      ></button>
      <nav class="cg-mkt-mobile-nav__panel" aria-label="Mobile">
        <div class="cg-mkt-mobile-nav__head">
          <span class="cg-mkt-mobile-nav__title">Menu</span>
          <button
            type="button"
            class="cg-mkt-mobile-nav__close"
            aria-label="Close menu"
            (click)="closeMobileMenu()"
          >
            ×
          </button>
        </div>

        <a
          routerLink="/"
          class="cg-mkt-mobile-nav__link"
          routerLinkActive="cg-mkt-mobile-nav__link--active"
          [routerLinkActiveOptions]="{ exact: true }"
          (click)="closeMobileMenu()"
          >Home</a
        >

        <div class="cg-mkt-mobile-nav__group">
          <button
            type="button"
            class="cg-mkt-mobile-nav__link cg-mkt-mobile-nav__trigger"
            [attr.aria-expanded]="mobileDestinationsOpen()"
            (click)="toggleMobileDestinations()"
          >
            Destinations
            <span class="cg-mkt-nav__chevron" aria-hidden="true">▾</span>
          </button>
          @if (mobileDestinationsOpen()) {
            <div class="cg-mkt-mobile-nav__sub">
              @if (countries().length > 0) {
                @for (c of countries(); track c.slug) {
                  <a
                    [routerLink]="['/countries', c.slug]"
                    class="cg-mkt-mobile-nav__sublink"
                    (click)="closeMobileMenu()"
                    >{{ c.name }}</a
                  >
                }
              } @else {
                <p class="cg-mkt-mobile-nav__empty">No published destinations yet.</p>
              }
            </div>
          }
        </div>

        <a
          routerLink="/success-stories"
          class="cg-mkt-mobile-nav__link"
          routerLinkActive="cg-mkt-mobile-nav__link--active"
          (click)="closeMobileMenu()"
          >Success Stories</a
        >
        <a
          routerLink="/testimonials"
          class="cg-mkt-mobile-nav__link"
          routerLinkActive="cg-mkt-mobile-nav__link--active"
          (click)="closeMobileMenu()"
          >Testimonials</a
        >
        <a
          routerLink="/blogs"
          class="cg-mkt-mobile-nav__link"
          routerLinkActive="cg-mkt-mobile-nav__link--active"
          (click)="closeMobileMenu()"
          >Blogs</a
        >
        @if (variant === 'country') {
          <a href="#universities" class="cg-mkt-mobile-nav__link" (click)="closeMobileMenu()"
            >Universities</a
          >
        }

        <div class="cg-mkt-mobile-nav__ctas">
          <a routerLink="/auth/login" class="cg-mkt-btn cg-mkt-btn--ghost" (click)="closeMobileMenu()"
            >Admin</a
          >
          <a href="#cta" class="cg-mkt-btn cg-mkt-btn--primary" (click)="closeMobileMenu()"
            >Get started</a
          >
        </div>
      </nav>
    </div>
  `,
})
export class MarketingHeaderComponent {
  private readonly countriesService = inject(CountriesService);
  private readonly platformId = inject(PLATFORM_ID);

  @ViewChild('destinationsRoot') destinationsRoot?: ElementRef<HTMLElement>;

  @Input() variant: 'home' | 'country' = 'home';

  readonly countries = signal<NavCountry[]>([]);
  readonly destinationsOpen = signal(false);
  readonly mobileMenuOpen = signal(false);
  readonly mobileDestinationsOpen = signal(false);
  readonly showDevBadge = !environment.production;

  constructor() {
    this.loadCountries();
  }

  toggleDestinations(event: Event): void {
    event.stopPropagation();
    this.destinationsOpen.update((open) => !open);
  }

  toggleMobileDestinations(): void {
    this.mobileDestinationsOpen.update((open) => !open);
  }

  toggleMobileMenu(): void {
    this.mobileMenuOpen.update((open) => {
      const next = !open;
      this.syncBodyScrollLock(next);
      if (!next) {
        this.mobileDestinationsOpen.set(false);
      }
      return next;
    });
    this.destinationsOpen.set(false);
  }

  closeMobileMenu(): void {
    if (!this.mobileMenuOpen()) {
      return;
    }
    this.mobileMenuOpen.set(false);
    this.mobileDestinationsOpen.set(false);
    this.syncBodyScrollLock(false);
  }

  onMenuLinkClick(): void {
    this.destinationsOpen.set(false);
    this.closeMobileMenu();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const root = this.destinationsRoot?.nativeElement;
    if (root?.contains(event.target as Node)) {
      return;
    }
    this.destinationsOpen.set(false);
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.destinationsOpen.set(false);
    this.closeMobileMenu();
  }

  private syncBodyScrollLock(locked: boolean): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    document.body.classList.toggle('cg-mkt-menu-open', locked);
  }

  private loadCountries(): void {
    this.countriesService.listPublicCountries().subscribe({
      next: (list) =>
        this.countries.set(
          list.map((c) => ({
            slug: c.slug,
            name: c.name,
          })),
        ),
      error: () => this.countries.set([]),
    });
  }
}
